import { FormEvent } from 'react'
import { Page, PageHeader, Section } from '@/components/page'
import { useForm } from '@inertiajs/react'
import { WizardGuide } from '@/components/layout/WizardGuide'
import { WizardLayout } from '../WizardLayout'
import { WizardActions } from '../WizardActions'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useBasePath } from '@/lib/paths'
import { ArrowRight, Download, Eye, FileText, ShieldCheck } from 'lucide-react'
import { Tip } from '@/components/sketch/Tip'
import type { WizardFormationRef } from '@/types'

type Document = {
  id: string
  code: string
  name: string
  principal_name: string | null
  execution_phase: 'pre_submission' | 'state_filing' | 'post_acceptance' | 'tax_filing'
  execution_method: 'typed' | 'wet' | 'staff' | 'none'
  signer_rule: string | null
  visibility: string
  preview_path: string
}

type Group = { category: string; documents: Document[] }

type Package = {
  revision: number
  manifest_digest: string
  status: string
  generated_at: string | null
  document_count: number
  download_path: string
}

type Props = {
  formation: WizardFormationRef
  ready: boolean
  reviewed: boolean
  package: Package | null
  groups: Group[]
}

const PHASE_LABELS: Record<Document['execution_phase'], string> = {
  pre_submission: 'Before submission',
  state_filing: 'State filing',
  post_acceptance: 'After acceptance',
  tax_filing: 'Tax filing',
}

const METHOD_LABELS: Record<Document['execution_method'], string> = {
  typed: 'Typed signature',
  wet: 'Wet ink signature',
  staff: 'Corppy handles',
  none: 'No signature',
}

const SIGNER_LABELS: Record<string, string> = {
  incorporator: 'Incorporator',
  organizer: 'Organizer',
  director: 'Directors',
  secretary: 'Secretary',
  registered_agent: 'Registered agent',
  stockholder: 'Stockholder(s)',
  member: 'Member(s)',
  manager: 'Manager(s)',
}

function signerLabel(rule: string | null) {
  if (!rule) return null
  return SIGNER_LABELS[rule] || rule
}

export default function DocumentsEdit({ formation, ready, reviewed, package: pkg, groups }: Props) {
  const basePath = useBasePath()
  const form = useForm({ documents_reviewed: false })

  const totalDocuments = groups.reduce((sum, group) => sum + group.documents.length, 0)
  const canContinue = ready && form.data.documents_reviewed

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    form.patch(`${basePath}/formations/${formation.id}/wizard/documents`)
  }

  return (
    <WizardLayout
      title="Documents"
      currentStep="documents"
      formationId={formation.id}
      resumeStep={formation.current_wizard_step}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'Documents' }]}
      guide={
        <WizardGuide title="Your document package">
          <p>
            These are the documents for your company. Review them before you sign — nothing is filed or charged yet.
          </p>
          <p>
            Corppy prepares the formation package from your application facts and the rules for your filing state.
            Corppy does not provide legal or tax advice.
          </p>
        </WizardGuide>
      }
      tips={
        <Tip seed="documents-package">
          Check names, ownership, roles and addresses carefully before you continue.
        </Tip>
      }
    >
      <Page>
        <PageHeader
          title={<>Your documents</>}
          subtitle={
            <>
              {formation.legal_name ?? 'Your company'} — {totalDocuments} documents
              {pkg ? ` · package ${pkg.revision} · ${pkg.manifest_digest}` : ''}
            </>
          }
          actions={ready && pkg ? (
            <Button asChild variant="outline">
              <a href={pkg.download_path}>
                <Download className="size-4" />
                Download all
              </a>
            </Button>
          ) : undefined}
        />

        <div
          role="note"
          className="flex items-start gap-3 rounded-lg border bg-muted/40 p-4"
        >
          <ShieldCheck className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
          <div className="text-sm text-foreground">
            <p className="font-medium">Prepared from your application</p>
            <p className="mt-1 text-muted-foreground">
              Review the complete package before continuing. Signing and state filing happen in their own steps.
            </p>
          </div>
        </div>

        {!ready && (
          <div role="status" className="rounded-lg border bg-secondary/50 p-4 text-sm text-muted-foreground">
            We’re preparing your document package.
          </div>
        )}

        {groups.map((group) => (
          <Section key={group.category} title={group.category}>
            <div className="rounded-lg border divide-y">
              {group.documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {doc.name}
                        {doc.principal_name && (
                          <span className="text-muted-foreground"> — {doc.principal_name}</span>
                        )}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {PHASE_LABELS[doc.execution_phase]} · {METHOD_LABELS[doc.execution_method]}
                        {signerLabel(doc.signer_rule) ? ` · ${signerLabel(doc.signer_rule)}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button asChild variant="outline" size="sm">
                      <a href={doc.preview_path} target="_blank" rel="noreferrer">
                        <Eye className="size-3.5" />
                        View
                      </a>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        ))}

        {ready && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="documents_reviewed"
                checked={form.data.documents_reviewed || reviewed}
                disabled={reviewed}
                onCheckedChange={(checked) => form.setData('documents_reviewed', checked === true)}
              />
              <Label htmlFor="documents_reviewed" className="text-sm text-foreground">
                I acknowledge I've reviewed my formation document package.
              </Label>
            </div>
            <WizardActions
              currentStep="documents"
              formationId={formation.id}
              disabled={form.processing}
              className="flex flex-wrap items-center gap-2"
            >
              <Button type="submit" disabled={form.processing || !canContinue}>
                {form.processing ? 'Saving…' : 'Continue to signature'}
                <ArrowRight className="size-4" />
              </Button>
            </WizardActions>
          </form>
        )}
        {!ready && <WizardActions currentStep="documents" formationId={formation.id} />}
      </Page>
    </WizardLayout>
  )
}
